import React, { useEffect, useState } from 'react';
import { PlusIcon } from '@heroicons/react/outline';
import { ApolloError } from '@apollo/client';
import { PatientContact } from '../../../../../generated/graphql';
import { usePatientProfileContext } from '../../';
import Modal from '../../../../components/Modal';
import { customToast } from '../../../../components/ToastAlert/customToast';
import Button from '../../../../components/Button';
import { logger } from '../../../../../lib/logger';
import {
  useProviderCreatePatientContactMutation,
  useProviderUpdatePatientContactMutation,
  useProviderDeletePatientContactMutation,
  useContactDeletionImpactQuery,
} from '../../../../../generated/graphql';
import ContactCard from './ContactCard';
import ContactForm from './ContactForm';
import SendFormToExternalModal from '../../../../components/Modals/SendFormToExternalModal';
import ViewLinksModal from '../../../../components/Modals/ViewLinksModal';
import ViewSentNotesModal from '../../../../components/Modals/ViewSentNotesModal';
import Alert from '../../../../components/Alert';
import { ContactFormData, getContactFullName } from './constants';

const Contacts: React.FC = () => {
  const { patient, refetchPatient } = usePatientProfileContext();
  const contacts = patient?.contacts || [];

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<PatientContact | null>(
    null,
  );
  const [modalTitle, setModalTitle] = useState('Add Contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<PatientContact | null>(
    null,
  );
  const [isSendFormModalOpen, setIsSendFormModalOpen] = useState(false);
  const [sendFormPreselectedContactId, setSendFormPreselectedContactId] =
    useState<string | null>(null);
  const [isViewLinksModalOpen, setIsViewLinksModalOpen] = useState(false);
  const [viewLinksContact, setViewLinksContact] =
    useState<PatientContact | null>(null);
  const [isViewSentNotesModalOpen, setIsViewSentNotesModalOpen] =
    useState(false);
  const [viewSentNotesContact, setViewSentNotesContact] =
    useState<PatientContact | null>(null);

  // Track cleanup timeout to prevent state race on rapid modal open/close
  const [cleanupTimeoutId, setCleanupTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  // GraphQL Mutations
  const [createContactMutation] = useProviderCreatePatientContactMutation();
  const [updateContactMutation] = useProviderUpdatePatientContactMutation();
  const [deleteContactMutation] = useProviderDeletePatientContactMutation();

  const { data: deletionImpactData, loading: deletionImpactLoading } =
    useContactDeletionImpactQuery({
      variables: { contactId: contactToDelete?.id ?? '' },
      skip: !isDeleteModalOpen || !contactToDelete?.id,
      fetchPolicy: 'network-only',
    });
  const deletionImpact = deletionImpactData?.contactDeletionImpact;
  const hasActiveLinksToRevoke = Boolean(deletionImpact?.hasActiveLinks);

  // Clear editing contact and modal title after modal closes
  // (delayed to prevent title flash during close animation)
  useEffect(() => {
    if (!isFormModalOpen) {
      const timeoutId = setTimeout(() => {
        setEditingContact(null);
        setModalTitle('Add Contact');
        setCleanupTimeoutId(null);
      }, 200); // Match MODAL_TRANSITION_DURATION
      setCleanupTimeoutId(timeoutId);
      return () => clearTimeout(timeoutId);
    }
  }, [isFormModalOpen]);

  // Handle opening the form for adding a new contact
  const handleAddContact = () => {
    // Clear any pending cleanup timeout to prevent state race
    if (cleanupTimeoutId) {
      clearTimeout(cleanupTimeoutId);
      setCleanupTimeoutId(null);
    }
    setEditingContact(null);
    setModalTitle('Add Contact');
    setIsFormModalOpen(true);
  };

  // Handle opening the form for editing an existing contact
  const handleEditContact = (contact: PatientContact) => {
    // Clear any pending cleanup timeout to prevent state race
    if (cleanupTimeoutId) {
      clearTimeout(cleanupTimeoutId);
      setCleanupTimeoutId(null);
    }
    setEditingContact(contact);
    setModalTitle('Edit Contact');
    setIsFormModalOpen(true);
  };

  // Handle closing the form modal
  const handleCloseForm = () => {
    setIsFormModalOpen(false);
    // Don't clear editingContact or modalTitle here - wait for modal to close
    // to prevent title change during close animation
  };

  // Handle form submission (create or update)
  const handleSubmitContact = async (data: ContactFormData) => {
    try {
      setIsSubmitting(true);

      // Perform the mutation
      if (editingContact) {
        if (!editingContact.id) {
          throw new Error('Cannot update contact: missing contact ID');
        }
        await updateContactMutation({
          variables: {
            input: {
              contactId: editingContact.id,
              contactType: data.contactType,
              firstName: data.firstName.trim(),
              lastName: data.lastName.trim(),
              email: data.email?.trim() || null,
              phoneNumber: data.phoneNumber.trim() || null,
              addressStreet: data.addressStreet?.trim() || null,
              addressCity: data.addressCity?.trim() || null,
              addressState: data.addressState?.trim() || null,
              addressPostalCode: data.addressPostalCode?.trim() || null,
              addressCountry: data.addressCountry?.trim() || null,
              relationshipDescription:
                data.relationshipDescription?.trim() || null,
            },
          },
        });
      } else {
        if (!patient?.id) {
          throw new Error('Cannot create contact: missing patient ID');
        }
        await createContactMutation({
          variables: {
            input: {
              patientId: patient.id,
              contactType: data.contactType,
              firstName: data.firstName.trim(),
              lastName: data.lastName.trim(),
              email: data.email?.trim() || '', // Email is required for create
              phoneNumber: data.phoneNumber.trim() || '',
              addressStreet: data.addressStreet?.trim() || null,
              addressCity: data.addressCity?.trim() || null,
              addressState: data.addressState?.trim() || null,
              addressPostalCode: data.addressPostalCode?.trim() || null,
              addressCountry: data.addressCountry?.trim() || null,
              relationshipDescription:
                data.relationshipDescription?.trim() || null,
            },
          },
        });
      }

      // Mutation succeeded, now try refetch
      try {
        await refetchPatient();
      } catch (refetchError) {
        logger.error('Error refetching patient after contact save', {
          error: refetchError,
          contactId: editingContact?.id,
          patientId: patient?.id,
        });
        customToast.success(
          editingContact
            ? 'Contact updated successfully'
            : 'Contact added successfully',
        );
        customToast.error(
          'There was an issue refreshing the data. Please refresh the page.',
        );
        handleCloseForm();
        return;
      }

      // Both succeeded
      customToast.success(
        editingContact
          ? 'Contact updated successfully'
          : 'Contact added successfully',
      );
      handleCloseForm();
    } catch (error) {
      // Actual mutation failure
      logger.error('Error saving contact', {
        error,
        contactId: editingContact?.id,
        patientId: patient?.id,
        operation: editingContact ? 'update' : 'create',
      });

      // Handle specific error types
      if (error instanceof ApolloError) {
        if (error.networkError) {
          customToast.error(
            'Network error. Please check your connection and try again.',
          );
          return;
        }
        if (
          error.graphQLErrors.some(
            (e) =>
              e.extensions?.code === 'PERMISSION_DENIED' ||
              e.extensions?.code === 'FORBIDDEN',
          )
        ) {
          customToast.error('You do not have permission to modify contacts.');
          return;
        }
      }

      customToast.error(
        editingContact
          ? 'Failed to update contact. Please try again.'
          : 'Failed to add contact. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deleting a contact
  const handleDeleteContact = (contact: PatientContact) => {
    if (!contact.id) {
      customToast.error('Cannot delete contact: missing contact ID');
      return;
    }
    setContactToDelete(contact);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete contact
  const confirmDeleteContact = async () => {
    if (!contactToDelete?.id) {
      return;
    }

    try {
      setIsSubmitting(true);

      await deleteContactMutation({
        variables: {
          input: {
            contactId: contactToDelete.id,
          },
        },
      });

      // Mutation succeeded, now try refetch
      try {
        await refetchPatient();
      } catch (refetchError) {
        logger.error('Error refetching patient after contact delete', {
          error: refetchError,
          contactId: contactToDelete.id,
          patientId: patient?.id,
        });
        customToast.success('Contact deleted successfully');
        customToast.error(
          'There was an issue refreshing the data. Please refresh the page.',
        );
        setIsDeleteModalOpen(false);
        setContactToDelete(null);
        return;
      }

      // Both succeeded
      customToast.success('Contact deleted successfully');
      setIsDeleteModalOpen(false);
      setContactToDelete(null);
    } catch (error) {
      logger.error('Error deleting contact', {
        error,
        contactId: contactToDelete.id,
        patientId: patient?.id,
      });

      if (error instanceof ApolloError) {
        if (error.networkError) {
          customToast.error(
            'Network error. Please check your connection and try again.',
          );
          return;
        }
        if (
          error.graphQLErrors.some(
            (e) =>
              e.extensions?.code === 'PERMISSION_DENIED' ||
              e.extensions?.code === 'FORBIDDEN',
          )
        ) {
          customToast.error('You do not have permission to delete contacts.');
          return;
        }
      }

      customToast.error('Failed to delete contact. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setContactToDelete(null);
  };

  const handleSendForm = (contact: PatientContact) => {
    // Clear any pending cleanup timeout to prevent state race
    if (cleanupTimeoutId) {
      clearTimeout(cleanupTimeoutId);
      setCleanupTimeoutId(null);
    }
    setSendFormPreselectedContactId(contact.id ?? null);
    setIsSendFormModalOpen(true);
  };

  const handleViewLinks = (contact: PatientContact) => {
    if (cleanupTimeoutId) {
      clearTimeout(cleanupTimeoutId);
      setCleanupTimeoutId(null);
    }
    setViewLinksContact(contact);
    setIsViewLinksModalOpen(true);
  };

  const handleViewSentNotes = (contact: PatientContact) => {
    if (cleanupTimeoutId) {
      clearTimeout(cleanupTimeoutId);
      setCleanupTimeoutId(null);
    }
    setViewSentNotesContact(contact);
    setIsViewSentNotesModalOpen(true);
  };

  const programId = patient?.programInstanceId ?? '';

  return (
    <>
      <div
        id="contacts"
        className="mt-4 flex w-full flex-col rounded-md border border-neutral-50 pb-6 pt-6"
      >
        {/* Header with Add button */}
        <div className="mb-4 flex w-full items-center justify-between px-6">
          <span className="text-body font-medium text-green-125">Contacts</span>
          <Button
            title="Add Contact"
            size="small"
            theme="primary"
            IconComponent={PlusIcon}
            onClick={handleAddContact}
          />
        </div>

        {/* Contacts List or Empty State */}
        {contacts.filter((c) => c.id).length === 0 ? (
          <div className="mx-6 flex flex-col items-center justify-center rounded border-2 border-dashed border-neutral-50 bg-neutral-25 px-6 py-12">
            <p className="text-body text-neutral-125">No contacts added yet</p>
            <p className="mt-2 text-caption text-neutral-110">
              Add emergency contacts, guardians, or support persons
            </p>
          </div>
        ) : (
          <div className="space-y-4 px-6">
            {contacts.map((contact, index) => {
              if (!contact.id) {
                logger.error('Contact missing ID in list', {
                  contact,
                  patientId: patient?.id,
                });
                return null;
              }
              return (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onEdit={handleEditContact}
                  onDelete={handleDeleteContact}
                  onSendForm={handleSendForm}
                  onViewLinks={handleViewLinks}
                  onViewSentNotes={handleViewSentNotes}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} setClosed={cancelDelete} width="small">
        <div className="flex flex-col items-center px-6 py-6">
          <p className="text-body text-neutral-150">
            Are you sure you want to delete this contact?
          </p>
          <p className="mt-2 text-caption text-neutral-125">
            {contactToDelete && getContactFullName(contactToDelete)
              ? getContactFullName(contactToDelete)
              : 'This contact'}{' '}
            will be permanently removed.
          </p>
          {deletionImpactLoading ? (
            <p className="mt-4 text-caption text-neutral-125">
              Checking active shared links...
            </p>
          ) : hasActiveLinksToRevoke ? (
            <Alert
              className="mt-4 w-full"
              level="warning"
              message="Deleting this contact will also revoke these active links:"
            >
              <ul className="mt-2 list-disc space-y-1 pl-5 text-caption">
                {deletionImpact?.activeNoteShares.map((share) => (
                  <li key={share.id}>Note: {share.noteTitle}</li>
                ))}
                {deletionImpact?.activeFormInvites.map((invite) => (
                  <li key={invite.id}>Form: {invite.formName}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <div className="mt-6 flex gap-3">
            <Button
              title="Cancel"
              size="small"
              theme="secondary"
              onClick={cancelDelete}
            />
            <Button
              title={isSubmitting ? 'Deleting...' : 'Delete'}
              size="small"
              theme="primary"
              onClick={confirmDeleteContact}
              disabled={isSubmitting || deletionImpactLoading}
            />
          </div>
        </div>
      </Modal>

      <SendFormToExternalModal
        isOpen={isSendFormModalOpen}
        setClosed={() => {
          setIsSendFormModalOpen(false);
          setSendFormPreselectedContactId(null);
        }}
        programId={programId}
        patientFirstName={patient?.firstName ?? undefined}
        preselectedContactId={sendFormPreselectedContactId ?? undefined}
      />

      <ViewLinksModal
        isOpen={isViewLinksModalOpen}
        setClosed={() => {
          setIsViewLinksModalOpen(false);
          setViewLinksContact(null);
        }}
        contact={viewLinksContact}
      />

      <ViewSentNotesModal
        isOpen={isViewSentNotesModalOpen}
        setClosed={() => {
          setIsViewSentNotesModalOpen(false);
          setViewSentNotesContact(null);
        }}
        contact={viewSentNotesContact}
      />

      {/* Contact Form Modal */}
      <Modal
        isOpen={isFormModalOpen}
        setClosed={handleCloseForm}
        width="medium"
        overflow="auto"
        hasCloseRow
        closeRowText={modalTitle}
      >
        <ContactForm
          contact={editingContact}
          onSubmit={handleSubmitContact}
          isSubmitting={isSubmitting}
        />
      </Modal>
    </>
  );
};

export default Contacts;
